import { registerActions } from "../actionRegistry";
import { toastSuccess, toastError } from "../ui/messages";
import { api } from "../api";
// Local timer state
let updatedownloadstatustimer: ReturnType<typeof setTimeout> | undefined;
import { getDomElement, show, hide, fadeIn, fadeOut } from "../utils/dom";
import {
  mountDownloadCart,
  mountDownloadProgress,
  unmountDownloadCart,
} from "./components/DownloadCart";
import { ID_DOWNLOAD_CART_COUNT } from "../utils/elementIds";

interface DownloadCartItem {
  id: number;
  lowResPath: string;
  title: string;
  width: number;
  height: number;
}
export interface DownloadCartResponse {
  [key: string]: unknown;
  state: string;
  settings?: { interior: boolean; dlRes: string; dlResCustom: string };
  items?: DownloadCartItem[];
  ids?: string;
  perc?: number;
  /** Which rendition the enhanced downloader will deliver. Declared rather than left to the
   *  index signature above, which would type it `unknown` at every use. */
  browserRendition?: "ORIGINAL" | "MEDIUM" | "LOW";
}

export async function toggleDownloadCart(): Promise<void> {
  const cart = getDomElement("download-cart");
  if (!cart) return;

  if (cart.style.display === "block") {
    if (getDomElement("download-finished")) {
      fadeOut("download-cart-button");
      const countEl = getDomElement(ID_DOWNLOAD_CART_COUNT);
      if (countEl) countEl.innerHTML = "0";
      unmountDownloadCart();
    }
    clearTimeout(updatedownloadstatustimer);
    cart.style.display = "none";
    hide("download-cart-back");
    return;
  }

  cart.style.display = "block";
  show("download-cart-back");

  const response = await api.downloadCart.getCart();

  mountDownloadCart(response);
  if (response.state === "downloading") {
    void updateDownloadStatus();
  }
}

export async function updateDownloadStatus(): Promise<void> {
  clearTimeout(updatedownloadstatustimer);

  // Stop polling if the cart is not visible
  const cart = getDomElement("download-cart");
  if (cart?.style.display !== "block") return;

  if (!getDomElement("download-cart-download-in-progress")) return;

  let response: Awaited<ReturnType<typeof api.downloadCart.downloadStatus>>;
  try {
    response = await api.downloadCart.downloadStatus();
  } catch {
    return;
  }

  const container = getDomElement("download-cart-download-in-progress");
  if (!container) return;

  mountDownloadProgress(container, response);

  // Stop polling on every terminal state, not just "completed". A failed or
  // stalled job used to keep the 2s loop alive for the life of the page, which
  // is what an infinite spinner looks like to the user.
  if (
    response.state === "completed" ||
    response.state === "failed" ||
    response.state === "stalled" ||
    response.state === "none"
  ) {
    return;
  }

  updatedownloadstatustimer = setTimeout(() => {
    void updateDownloadStatus();
  }, 2000);
}

export async function multiAddDownloadcart(type: string, id: string): Promise<void> {
  try {
    await api.downloadCart.addMulti({ type: type as "list" | "ids", id });
    toastSuccess("Artworks added to downloader");
    void downloadCartUpdate();
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function downloadCartUpdate(): Promise<void> {
  try {
    const response = await api.downloadCart.count();
    if (response.artworks > 0) {
      fadeIn("download-cart-button");
      const countEl = getDomElement(ID_DOWNLOAD_CART_COUNT);
      if (countEl) countEl.innerHTML = String(response.artworks);
    } else {
      fadeOut("download-cart");
      fadeOut("download-cart-button");
      hide("download-cart-back");
      const countEl = getDomElement(ID_DOWNLOAD_CART_COUNT);
      if (countEl) countEl.innerHTML = "0";
    }
  } catch {
    // silent
  }
}

export async function clearDownloadCart(): Promise<void> {
  try {
    await api.downloadCart.clear();
  } catch {
    return;
  }
  fadeOut("download-cart");
  fadeOut("download-cart-button");
  hide("download-cart-back");
  const countEl = getDomElement(ID_DOWNLOAD_CART_COUNT);
  if (countEl) countEl.innerHTML = "0";
}

registerActions({
  toggleDownloadCart,
  multiAddDownloadcart,
  downloadCartUpdate,
});
